/* @flow */
import cx from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import { QuickActionUIDs } from 'entities/QuickActions';
import type { QuickActionType } from 'entities/QuickActions';
import QuickActionsBarItem, { widthForAction } from 'chat/components/QuickActionsBarItem.jsx';
import { memoize, range, rangeRight } from 'lodash';

import './styles/QuickActionsBar.scss';

type Props = {
  actions: QuickActionType[],
  group: Group,
}

type State = {
  scrollIndex: number,
  scrollOffset: number,
  numVisible: number,
}

const quickActionsBarItemMargin = 0;
const arrowWidth = 30;
const edgeOffset = 1.5 * arrowWidth;

export default class QuickActionsBar extends React.Component {
  props: Props;
  state: State = {
    scrollIndex: 0,
    scrollOffset: 0,
    numVisible: 0,
  };

  componentDidMount() {
    this.resetSize();
  }

  componentDidUpdate(prevProps: Props) {
    // Will be a new Array when actions change
    if (this.props.actions !== prevProps.actions) {
      this.resetSize();
    }
  }

  resetSize() {
    this.barWidth = ReactDOM.findDOMNode(this).offsetWidth;
    this.contentWidth = this.offsetForIndex(this.props.actions.length) - quickActionsBarItemMargin;

    const maxScrollIndex: ?number = range(this.props.actions.length).find((index) => {
      return this.contentWidth
        - this.offsetForIndex(index)
        - quickActionsBarItemMargin
        <= this.barWidth;
    });
    if (maxScrollIndex || maxScrollIndex === 0) {
      this.maxScrollIndex = maxScrollIndex;
    } else {
      this.maxScrollIndex = this.props.actions.length - 1;
    }

    this.scrollToIndex(0);
  }

  maxScrollIndex: number = 0;
  barWidth: number = 0;
  contentWidth: number = 0;

  canCycleForwardFrom(index: number): boolean {
    return index < this.maxScrollIndex;
  }

  canCycleBackFrom(index: number): boolean {
    return index > 0;
  }

  cycleForward = () => {
    const amount = this.state.numVisible;
    const nextIndex = range(this.state.scrollIndex + amount, this.props.actions.length).find((index) => {
      return index <= this.maxScrollIndex &&
        this.props.actions[index].uid !== QuickActionUIDs.DIVIDER;
    });
    if (nextIndex || nextIndex === 0) {
      this.scrollToIndex(nextIndex);
    } else {
      this.scrollToIndex(this.maxScrollIndex);
    }
  };

  cycleBack = () => {
    const amount = this.state.numVisible;
    const nextIndex = range(this.state.scrollIndex - amount, -1, -1).find((index) => {
      return index >= 0 &&
        this.props.actions[index].uid !== QuickActionUIDs.DIVIDER;
    });
    if (nextIndex || nextIndex === 0) {
      this.scrollToIndex(nextIndex);
    } else {
      this.scrollToIndex(0);
    }
  };

  numVisibleForIndex(index: number): number {
    const lastVisible = rangeRight(index, this.props.actions.length)
      .find((i) => {
        return this.alignedOffsetForIndex(i)
          - this.alignedOffsetForIndex(index)
          + widthForAction(this.props.actions[index])
          + (index === this.maxScrollIndex ? 0 : edgeOffset)
          <= this.barWidth;
      });
    if (lastVisible || lastVisible === 0) {
      return lastVisible - index + 1;
    } else {
      return 0;
    }
  }

  scrollToIndex(index: number) {
    this.setState({
      scrollIndex: index,
      scrollOffset: this.alignedOffsetForIndex(index),
      numVisible: this.numVisibleForIndex(index),
    });
  }

  offsetForIndex = memoize((index: number): number => {
    if (index === 0) {
      return 0;
    } else {
      return this.offsetForIndex(index - 1)
        + widthForAction(this.props.actions[index - 1])
        + quickActionsBarItemMargin;
    }
  });

  alignedOffsetForIndex(index: number): number {
    if (index === 0) {
      return this.offsetForIndex(index);

    // Includes greater than in case we skip past a divider
    } else if (index >= this.maxScrollIndex) {
      return this.contentWidth - this.barWidth;

    // Show a bit of the previous item from under the chevron
    } else {
      return this.offsetForIndex(index) - edgeOffset;
    }
  }

  carouselOffset() {
    return { marginLeft: `${-this.state.scrollOffset}px` };
  }

  leftArrowClass(): string {
    return cx(
      'QuickActionsBar-arrow',
      'QuickActionsBar-arrow-left',
      { 'QuickActionsBar-arrow-hidden': !this.canCycleBackFrom(this.state.scrollIndex) }
    );
  }

  rightArrowClass(): string {
    return cx(
      'QuickActionsBar-arrow',
      'QuickActionsBar-arrow-right',
      { 'QuickActionsBar-arrow-hidden': !this.canCycleForwardFrom(this.state.scrollIndex) }
    );
  }

  isIndexVisible(index: number): bool {
    return index >= this.state.scrollIndex
      && index <= this.state.scrollIndex + this.state.numVisible;
  }

  render() {
    return (
      <div className="QuickActionsBar">
        <a
          className={this.leftArrowClass()}
          onClick={this.cycleBack}>
          <Glyph name="chevron-left" color="brand" size="medium" />
        </a>
        <a
          className={this.rightArrowClass()}
          onClick={this.cycleForward}>
          <Glyph name="chevron-right" color="brand" size="medium" />
        </a>
        <div className="QuickActionsBar-carousel">
          <div className="QuickActionsBar-carousel-content" style={this.carouselOffset()}>
            {this.props.actions.map((a, index) =>
              <QuickActionsBarItem
                action={a}
                group={this.props.group}
                visible={this.isIndexVisible(index)}
                key={index}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/chat/components/QuickActionsBar.jsx